import "../styles.scss"
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom"
import { Footer } from "../components/layout/Footer/Footer.component"
import MyProfile from "../pages/MyProfile/MyProfile"
import HomeHeader from "../components/layout/Header/Header.component"
import MyRides from "../pages/MyRides/MyRides"
import ResetPassword from "../pages/ResetPassword/ResetPassword"
import { useSiteInitialization } from "../hooks/useSiteInitialization"
import DriverRegistration from "../pages/DriverRegistration/DriverRegistration"
import Login from "../pages/login/Login"
import {
  hideModal,
  ModalTypes,
  selectActiveModal,
  selectSecondaryModal,
} from "../redux/slices/modalSlice"
import SimpleModal from "../components/Modals/SimpleModal/SimpleModal.component"
import { useAppDispatch, useAppSelector } from "../redux/hooks"
import { FC, ReactNode, useEffect } from "react"
import { useIsUserLoggedin } from "../hooks/useIsUserLoggedin"
import { ROUTES } from "../config/routes.const"
import { LANGUAGES_TYPE } from "../types/language.d"
import { theme } from "../main"
import i18n from "../i18n"
import NewReservation from "../pages/NewReservation/NewReservation"
import { Alert, Snackbar } from "@mui/material"
import { hideAlert } from "../redux/slices/alertSlice"
import { useTheme } from "../hooks/useTheme"
import Favicon from "react-favicon"
import { useBranding } from "../hooks/useBranding"
import { updateApiHeaders } from "../redux/api/apiConfig"
import { refreshUserByToken } from "../redux/api/authAPI"
type AuthRouteProps = {
  children: ReactNode
}

const clearLocalStorage = (isAuthenticated: any) => {
  const token = localStorage.getItem("x-Token")
  const xCustomer = localStorage.getItem("x-Customer")

  if (token && xCustomer) {
    if (!isAuthenticated) {
      localStorage.clear()
      updateApiHeaders(undefined, undefined)
    }
  }
}

const AuthenticatedRoute: FC<AuthRouteProps> = ({ children }) => {
  const isAuthenticated = useIsUserLoggedin()
  clearLocalStorage(isAuthenticated)

  return isAuthenticated ? <>{children}</> : <Navigate to={ROUTES.LOGIN} />
}

const UnauthenticatedRoute: FC<AuthRouteProps> = ({ children }) => {
  const isAuthenticated = useIsUserLoggedin()
  return !isAuthenticated ? <>{children}</> : <Navigate to={ROUTES.MY_RIDES} />
}

export const router = createBrowserRouter([
  {
    path: `${ROUTES.ROOT}`,
    element: <RootPage />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: (
          <UnauthenticatedRoute>
            <Login />
          </UnauthenticatedRoute>
        ),
      },
      {
        path: ROUTES.RESET_PASSWORD + "/:token",
        element: (
          <UnauthenticatedRoute>
            <ResetPassword />
          </UnauthenticatedRoute>
        ),
      },
      {
        path: ROUTES.DRIVER_REGISTRATION + "/:token",
        element: (
          <UnauthenticatedRoute>
            <DriverRegistration />
          </UnauthenticatedRoute>
        ),
      },
      {
        path: ROUTES.PROFILE,
        element: (
          <AuthenticatedRoute>
            <MyProfile />
          </AuthenticatedRoute>
        ),
      },
      {
        path: ROUTES.MY_RIDES,
        element: (
          <AuthenticatedRoute>
            <MyRides />
          </AuthenticatedRoute>
        ),
      },
      {
        path: ROUTES.NEW_RESERVATION,
        element: (
          <AuthenticatedRoute>
            <NewReservation />
          </AuthenticatedRoute>
        ),
      },
      {
        path: ROUTES.MONTHLY_SUMMARY,
        element: (
          <AuthenticatedRoute>
            <>monthlySummarymonthlySummarymonthlySummarymonthlySummary</>
          </AuthenticatedRoute>
        ),
      },
      {
        path: "account/*",
        element: (
          <AuthenticatedRoute>
            <Navigate to={ROUTES.MY_RIDES} />
          </AuthenticatedRoute>
        ),
      },
      {
        path: "/*",
        element: (
          <UnauthenticatedRoute>
            <Navigate to={ROUTES.LOGIN} />
          </UnauthenticatedRoute>
        ),
      },
    ],
  },
])

export function RootPage() {
  useTheme()
  const { favicon } = useBranding()
  const { isLoading } = useSiteInitialization()
  const activeModal: ModalTypes | null = useAppSelector(selectActiveModal)
  const secondaryModal: ModalTypes | null = useAppSelector(selectSecondaryModal)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { isOpen, type, text } = useAppSelector((state) => state.alert)
  useEffect(() => {
    if (location.pathname === ROUTES.IMPERSONATE) {
      if (typeof location.search === "string") {
        const splitedQuery = location.search.split("=")
        if (splitedQuery.length === 2) {
          refreshUserByToken(splitedQuery[1])
          return
        }
      }
    }
    if (location.pathname === ROUTES.ROOT) {
      navigate(ROUTES.LOGIN)
    }
  }, [navigate, location.pathname])

  useEffect(() => {
    const titles: Record<string, string> = {
      "/": "Home - My App",
      "/about": "About Us - My App",
      "/contact": "Contact - My App",
    }

    document.title = location.pathname.split("/").reduce((acc, path) => {
      if (path === "") return acc
      return path
    })
  }, [location.pathname])

  return (
    <>
      <Favicon url={favicon} />
      <HomeHeader />
      <div className="app-content">
        {isLoading ? <div>Loading...</div> : <Outlet />}
      </div>
      <Footer />
      <Snackbar
        open={isOpen}
        autoHideDuration={4000}
        onClose={() => dispatch(hideAlert())}
      >
        <Alert
          onClose={() => dispatch(hideAlert())}
          severity={type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {text}
        </Alert>
      </Snackbar>

      {activeModal && (
        <>
          <SimpleModal
            open={activeModal === ModalTypes.SIMPLE_MODAL}
            handleClose={() => dispatch(hideModal())}
          />
          <SimpleModal
            open={secondaryModal === ModalTypes.SIMPLE_MODAL}
            handleClose={() => dispatch(hideModal())}
          />
        </>
      )}
    </>
  )
}
